import React, { useMemo, useState } from 'react'
import { Layout, Menu, Button, Tag, Typography, Grid } from 'antd'
import type { MenuProps } from 'antd'
import {
  DashboardOutlined,
  UnorderedListOutlined,
  GiftOutlined,
  PayCircleOutlined,
  StopOutlined,
  MenuOutlined,
  LogoutOutlined,
  AuditOutlined,
  TeamOutlined,
  SafetyOutlined,
  SettingOutlined,
  BarChartOutlined,
  FileTextOutlined,
  ShopOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore, hasAnyRole, roleLabel, ROLE_COLORS, AdminRole } from '../stores/auth'

const { Sider, Header, Content } = Layout
const { Text } = Typography

interface MenuEntry {
  key: string
  label: string
  icon: React.ReactNode
  roles: AdminRole[]
}

interface MenuGroup {
  key: string
  label: string
  children: MenuEntry[]
}

const ALL_ROLES: AdminRole[] = ['super_admin', 'admin', 'operator', 'finance', 'finance_manager', 'leasing_director', 'leasing_manager', 'rental_consultant']
const OPS_ROLES: AdminRole[] = ['super_admin', 'admin', 'operator']
const FIN_ROLES: AdminRole[] = ['super_admin', 'admin', 'finance', 'finance_manager']
const ADMIN_ROLES: AdminRole[] = ['super_admin', 'admin']
// R4 招商总监（LD）可见的招商域菜单。刻意独立于 OPS_ROLES —— OPS_ROLES 同时驱动
// /blacklist（黑名单 + HR 花名册，含员工 PII），就地扩容会把 PII 菜单误放给总监。
const LEASING_ROLES: AdminRole[] = [...OPS_ROLES, 'leasing_director', 'rental_consultant']
// R5 运营（leasing_manager）：招商域页面与 LD 同集（含黑名单/花名册查看，后端 BLACKLIST_VIEW 同步授予）。
// 不动 OPS_ROLES —— 见上行注释的 PII 红线仅约束 OPS_ROLES 的就地扩容；运营岗位经业务方裁定需要该入口。
// rental_consultant（租房顾问）：/leads 与 /project-intro 共用 LEASING_ROLES，保持零拆分就地追加。
// ST3（2026-10-20）：/blacklist 改用独立 BLACKLIST_MENU_ROLES（对齐后端 BLACKLIST_VIEW = SA/A/O/LM/RC，
// 但不含 LD——LEASING_ROLES 含 LD，就地复用会把黑名单 PII 菜单误放给总监）。
const BLACKLIST_MENU_ROLES: AdminRole[] = [...OPS_ROLES, 'leasing_manager', 'rental_consultant']
// ST3（2026-10-20）：答谢管理 / 成交收款菜单角色集 = FIN_ROLES + leasing_manager（运营经 REWARD_OPERATE
// 可查看/核算/导出入口，审批/发放/调额/收款修改由各自权限键门控，LM 不持有）。独立于 FIN_ROLES 避免误改财务域。
const FIN_VIEW_ROLES: AdminRole[] = [...FIN_ROLES, 'leasing_manager']

// 菜单按业务域分组（full_feature_v3）；「更多业务」为未来直租域预留入口位
const MENU_GROUPS: MenuGroup[] = [
  {
    key: 'g-ops',
    label: '推荐运营',
    children: [
      { key: '/dashboard', label: '工作台', icon: <DashboardOutlined />, roles: ALL_ROLES },
      { key: '/leads', label: '全部线索', icon: <UnorderedListOutlined />, roles: [...LEASING_ROLES, 'leasing_manager'] },
      { key: '/referrers', label: '推荐人', icon: <TeamOutlined />, roles: ALL_ROLES },
      { key: '/project-intro', label: '项目介绍', icon: <FileTextOutlined />, roles: LEASING_ROLES },
      // thankyou_v2b：「被推荐人礼遇」已并入「答谢方案」（/benefit-config），菜单项移除，
      // /referred-benefits 路由改为重定向（见 App.tsx）。
      // thankyou_v1（批次 2）：答谢配置入口（仅超管/管理员可见）；thankyou_v2b 更名「答谢方案」
      { key: '/benefit-config', label: '答谢方案', icon: <GiftOutlined />, roles: ADMIN_ROLES },
      // thankyou_v2b（批次 8.2）：商户档案（需求 4.4「每商家一套券」档案侧），仅超管/管理员
      { key: '/merchants', label: '商户档案', icon: <ShopOutlined />, roles: ADMIN_ROLES },
      // merge_projects_into_projectintro_v1：「项目管理」已并入「项目介绍」页的标签页，
      // 独立菜单项与 /projects 路由已移除（/projects 重定向到 /project-intro）。
      { key: '/review-center', label: '审核中心', icon: <AuditOutlined />, roles: [...ADMIN_ROLES, 'leasing_manager'] },
    ],
  },
  {
    key: 'g-fin',
    label: '财务',
    children: [
      { key: '/rewards', label: '答谢管理', icon: <GiftOutlined />, roles: FIN_VIEW_ROLES },
      { key: '/payments', label: '成交收款', icon: <PayCircleOutlined />, roles: FIN_VIEW_ROLES },
    ],
  },
  {
    key: 'g-risk',
    label: '风控',
    children: [
      { key: '/risk-control', label: '风控中心', icon: <SafetyOutlined />, roles: ADMIN_ROLES },
      { key: '/blacklist', label: '黑名单 / 花名册', icon: <StopOutlined />, roles: BLACKLIST_MENU_ROLES },
    ],
  },
  {
    key: 'g-sys',
    label: '系统',
    children: [
      { key: '/system-config', label: '系统配置', icon: <SettingOutlined />, roles: ALL_ROLES }, // account_self_v1：全角色可进（非 SA/A/LM 仅见账号/值班等只读 tab）
      { key: '/reports', label: '报表中心', icon: <BarChartOutlined />, roles: [...FIN_ROLES, 'leasing_director', 'leasing_manager'] },
    ],
  },
]

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': '工作台',
  '/leads': '全部线索',
  '/referrers': '推荐人管理',
  '/project-intro': '项目介绍',
  '/benefit-config': '答谢方案',
  '/merchants': '商户档案',
  '/review-center': '审核中心',
  '/rewards': '答谢管理',
  '/payments': '成交收款',
  '/risk-control': '风控中心',
  '/blacklist': '黑名单 / HR 花名册',
  '/system-config': '系统配置',
  '/reports': '报表中心',
}

const AdminLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [collapsed, setCollapsed] = useState(false)

  const menuItems: MenuProps['items'] = useMemo(() => {
    const groups = MENU_GROUPS.map((g) => {
      const children = g.children
        .filter((e) => hasAnyRole(user?.role, ...e.roles))
        .map((e) => ({ key: e.key, icon: e.icon, label: e.label }))
      return children.length > 0 ? { key: g.key, label: g.label, children } : null
    }).filter(Boolean) as NonNullable<MenuProps['items']>
    // 未来业务域预留入口位（禁用）
    return [
      ...groups,
      { key: 'g-future', label: '更多业务', children: [{ key: '__future__', label: '敬请期待', icon: <MoreOutlined />, disabled: true }] },
    ]
  }, [user?.role])

  const selectedKey =
    Object.keys(PAGE_TITLES).find((k) => location.pathname.startsWith(k)) ?? '/dashboard'

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={220}
        collapsed={isMobile ? true : collapsed}
        collapsedWidth={isMobile ? 0 : 72}
        trigger={null}
        style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
      >
        <div
          style={{
            padding: '18px 16px',
            color: '#333',
            fontSize: 15,
            fontWeight: 600,
            borderBottom: '1px solid #f0f0f0',
            lineHeight: 1.4,
          }}
        >
          {isMobile ? '留位计划后台' : (
            <>
              <span style={{ color: '#1A873B' }}>德高信留位计划</span>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>易联金融广场 · 管理后台</div>
            </>
          )}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderInlineEnd: 'none' }}
        />
        {!isMobile && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              padding: '14px 16px',
              borderTop: '1px solid #f0f0f0',
              color: '#666',
              fontSize: 12,
            }}
          >
            {!collapsed && (
              <>
                <div>{user?.username ?? '—'}</div>
                <Tag
                  style={{ marginTop: 6 }}
                  color={ROLE_COLORS[(user?.role ?? '') as AdminRole] ?? 'default'}
                >
                  {roleLabel(user?.role)}
                </Tag>
              </>
            )}
          </div>
        )}
      </Sider>
      <Layout>
        <Header
          style={{
            height: 56,
            lineHeight: '56px',
            background: '#fff',
            borderBottom: '1px solid #E8ECF0',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {!isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setCollapsed((c) => !c)}
                style={{ minWidth: 44, minHeight: 44 }}
                aria-label="折叠菜单"
              />
            )}
            <Text strong style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
              {PAGE_TITLES[selectedKey] ?? '管理后台'}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isMobile && (
              <Tag color={ROLE_COLORS[(user?.role ?? '') as AdminRole] ?? 'default'} style={{ marginInlineEnd: 0 }}>
                {roleLabel(user?.role)}
              </Tag>
            )}
            <Text type="secondary" style={{ fontSize: 12, display: isMobile ? 'none' : undefined }}>
              {user?.username}
            </Text>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{ minWidth: 44, minHeight: 44 }}
            >
              {isMobile ? '' : '登出'}
            </Button>
          </div>
        </Header>
        <Content style={{ padding: isMobile ? 12 : '20px 24px', background: '#F5F7FA' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminLayout
